import { safeStorage } from './safeStorage';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

const PIXEL_ID = '1675497490113190';

const generateEventId = (): string =>
  `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

// Id de evento para dedup navegador↔CAPI: se genera ANTES de llamar al
// backend, viaja en el header x-fb-event-id y se reusa en el track del Pixel.
export const newFbEventId = generateEventId;

// ── Meta click-id (fbc) / browser-id (fbp) handling ──────────────────────────
// The browser Pixel only writes the `_fbc` cookie when the visitor lands with a
// `?fbclid=` param, so fbc coverage drops for organic traffic and SPA edge
// cases (event fires before the cookie is written). These helpers persist the
// fbclid, rebuild `_fbc` when missing, and forward fbc/fbp to the Conversions
// API so server-side events carry the same identifiers as the Pixel.

const FBCLID_KEY = 'eva_fbclid';
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match[2]) : null;
};

// Call once on app load: persist the fbclid (so fbc survives later navigation
// where the URL param is gone) and seed the `_fbc` cookie if the Pixel hasn't
// written it yet, eliminating the race where an event fires before that.
export const captureFbclid = (): void => {
  if (typeof window === 'undefined') return;
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (!fbclid) return;
  const ts = Date.now();
  try {
    safeStorage.setItem(FBCLID_KEY, JSON.stringify({ fbclid, ts }));
  } catch {
    /* storage unavailable (private mode) — fall back to the cookie below */
  }
  if (!readCookie('_fbc')) {
    document.cookie = `_fbc=fb.1.${ts}.${fbclid}; Max-Age=${90 * 24 * 60 * 60}; Path=/; SameSite=Lax`;
  }
};

// Returns the fbc value, rebuilding it from a stored fbclid (<= 90 days old,
// per Meta guidance) when the `_fbc` cookie is absent.
export const getFbc = (): string | null => {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  try {
    const raw = safeStorage.getItem(FBCLID_KEY);
    if (raw) {
      const { fbclid, ts } = JSON.parse(raw);
      if (fbclid && typeof ts === 'number' && Date.now() - ts < NINETY_DAYS_MS) {
        return `fb.1.${ts}.${fbclid}`;
      }
    }
  } catch {
    /* ignore malformed storage */
  }
  return null;
};

export const getFbp = (): string | null => readCookie('_fbp');

// Headers that forward the click/browser ids to the backend so its Conversions
// API events are enriched and deduplicated against the browser Pixel.
export const fbHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const fbc = getFbc();
  const fbp = getFbp();
  if (fbc) headers['x-fbc'] = fbc;
  if (fbp) headers['x-fbp'] = fbp;
  return headers;
};

// fbHeaders + el event id compartido, para requests cuyo endpoint espeja el
// evento por CAPI (registro, crear examen/planeación/material/grupo).
export const fbEventHeaders = (eventId: string): Record<string, string> => ({
  ...fbHeaders(),
  'x-fb-event-id': eventId,
});

// Advanced Matching: tras conocer al usuario (login/registro/sesión guardada)
// se re-inicializa el Pixel con em + external_id (fbq los hashea antes de
// enviar). Sube la calidad de coincidencia de todos los eventos siguientes.
export const setPixelUserData = (email?: string | null, userId?: number | string | null): void => {
  if (typeof window === 'undefined' || !window.fbq) return;
  const data: Record<string, string> = {};
  if (email) data.em = email.trim().toLowerCase();
  if (userId !== undefined && userId !== null && userId !== '') data.external_id = String(userId);
  if (!Object.keys(data).length) return;
  window.fbq('init', PIXEL_ID, data);
};

export const trackEvent = (
  eventName: string,
  params?: Record<string, any>,
  options?: { custom?: boolean; eventId?: string },
): string => {
  const eventId = options?.eventId || generateEventId();
  if (typeof window !== 'undefined' && window.fbq) {
    // Standard events use 'track'; non-standard (custom) events use 'trackCustom'.
    const method = options?.custom ? 'trackCustom' : 'track';
    window.fbq(method, eventName, params || {}, { eventID: eventId });
  }
  return eventId;
};

// Custom event (no Meta standard equivalent): fired when the user lands on the
// registration form, before submitting. `source` distinguishes teacher vs. student.
export const trackStartRegistration = (source: string): string => {
  return trackEvent('StartRegistration', { content_name: source }, { custom: true });
};

export const trackCompleteRegistration = (email: string, eventId?: string): string => {
  return trackEvent(
    'CompleteRegistration',
    {
      content_name: 'teacher_registration',
      status: true,
    },
    { eventId },
  );
};

export const trackLead = (source: string): string => {
  return trackEvent('Lead', {
    content_name: source,
  });
};

export const trackStartTrial = (): string => {
  return trackEvent('StartTrial', {
    content_name: 'pro_trial',
  });
};

export const trackPurchase = (value: number, currency: string = 'MXN'): string => {
  return trackEvent('Purchase', {
    value,
    currency,
  });
};

// ── In-platform activation events (custom, no Meta standard equivalent) ─────
// Fired at the success point of each core flow so Ads Manager can build
// audiences/conversions on real product activation, not just registration.

export const trackCreateExam = (status: 'draft' | 'published', eventId?: string): string => {
  return trackEvent('CreateExam', { content_name: 'exam', status }, { custom: true, eventId });
};

export const trackCreatePlan = (planType: string, count: number = 1, eventId?: string): string => {
  return trackEvent('CreatePlan', { content_name: planType, num_items: count }, { custom: true, eventId });
};

export const trackCreateMaterial = (materialType: string, eventId?: string): string => {
  return trackEvent('CreateMaterial', { content_name: materialType }, { custom: true, eventId });
};

export const trackCreateGroup = (source: string, stage?: string, eventId?: string): string => {
  return trackEvent(
    'CreateGroup',
    { content_name: source, ...(stage ? { education_stage: stage } : {}) },
    { custom: true, eventId },
  );
};
